import { z } from 'zod';
import { webActionSchema } from './action.ts';
import { day, instant } from './primitives.ts';

/** Worst first. */
export const HEALTHS = ['down', 'attention', 'live', 'idle', 'off'] as const;

export type Health = (typeof HEALTHS)[number];

export const STAGES = ['market', 'analysts', 'trading', 'members', 'calendars', 'funds'] as const;

export type Stage = (typeof STAGES)[number];

/** Whether a card needs a person: `act` says what to do, `watch` mends itself or waits, `ok` is fine. */
const NEEDS = ['act', 'watch', 'ok'] as const;

const buttonSchema = z.object({ label: z.string(), action: webActionSchema });

export type SystemButton = z.infer<typeof buttonSchema>;

const factSchema = z.object({
  label: z.string(),
  value: z.string(),
  warn: z.boolean(),
});

const linkSchema = z.object({ label: z.string(), href: z.string() });

export type SystemLink = z.infer<typeof linkSchema>;

const integrationSchema = z.object({
  key: z.string(),
  stage: z.enum(STAGES),
  name: z.string(),
  vendor: z.string().nullable(),
  health: z.enum(HEALTHS),
  /** Absent reads as `ok`: Home's own cards are written without one. */
  need: z.enum(NEEDS).optional(),
  /** For an `act` card, what is wrong and what to do; otherwise what it last did. */
  said: z.string(),
  button: buttonSchema.nullable().optional(),
  evidence: z.object({ what: z.string(), at: instant }).nullable(),
  facts: z.array(factSchema),
  queues: z.array(z.string()),
  links: z.array(linkSchema),
});

export type Integration = z.infer<typeof integrationSchema>;

const runSchema = z.object({
  state: z.string(),
  createdAt: instant,
  finishedAt: instant.nullable(),
  error: z.string().nullable(),
});

const queueHealthSchema = z.object({
  name: z.string(),
  label: z.string(),
  ready: z.number().int(),
  active: z.number().int(),
  failed: z.number().int(),
  completedDay: z.number().int(),
  failedDay: z.number().int(),
  failing: z.boolean(),
  lastCompletedAt: instant.nullable(),
  lastFailure: runSchema.nullable(),
  schedules: z.array(z.object({ key: z.string(), cron: z.string(), said: z.string() })),
});

export type QueueHealth = z.infer<typeof queueHealthSchema>;

const SLOT_OUTCOMES = ['completed', 'failed', 'running', 'missed', 'due', 'rests'] as const;

export type SlotOutcome = (typeof SLOT_OUTCOMES)[number];

export const slotSchema = z.object({
  queue: z.string(),
  key: z.string(),
  label: z.string(),
  at: instant,
  outcome: z.enum(SLOT_OUTCOMES),
  error: z.string().nullable(),
});

export type DaySlot = z.infer<typeof slotSchema>;

const DAY_SPANS = ['mcx'] as const;

const deskDaySchema = z.object({
  day,
  from: instant,
  to: instant,
  spans: z.array(z.object({ kind: z.enum(DAY_SPANS), from: instant, to: instant })),
  slots: z.array(slotSchema),
  heartbeats: z.array(z.object({ queue: z.string(), label: z.string(), said: z.string() })),
});

export type DeskDay = z.infer<typeof deskDaySchema>;

export const systemsSchema = z.object({
  now: instant,
  startedAt: instant,
  integrations: z.array(integrationSchema),
  day: deskDaySchema,
});

export type Systems = z.infer<typeof systemsSchema>;
